import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import axiosInstance from '../../utils/axios';
import amplifyConfig from '../../config';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { DateComparisonOperator, EditMode, EditRow, ErrorResponse, InventoryJob, InventoryJobState, UserCheckCount } from '../../types/inventoryJob';

const initialState: InventoryJobState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  editMode: undefined,
  editData: undefined,
  isDialogOpen: false,
  dialogTitle: '',
  dialogInfo: [],
  deleteFlag: false,
  abortFlag: false,
  inventoryJobRowData: [],
  userCheckCount: undefined,
};

const slice = createSlice({
  name: 'InventoryJob',
  initialState,
  reducers: {
    startLoading(state: InventoryJobState) {
      state.isLoading = true;
    },

    hasError(state: InventoryJobState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: InventoryJobState) {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
    },

    setServerMessage(state: InventoryJobState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setEditMode(state: InventoryJobState, action: PayloadAction<EditMode>): void {
      state.editMode = action.payload;
    },

    setEditData(state: InventoryJobState, action: PayloadAction<InventoryJob>): void {
      state.editData = action.payload;
    },

    openDialog(state: InventoryJobState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: InventoryJobState): void {
      state.isDialogOpen = false;
    },

    setDialogTitle(state: InventoryJobState, action: PayloadAction<string>): void {
      state.dialogTitle = action.payload;
    },

    setDialogInfo(state: InventoryJobState, action: PayloadAction<EditRow[]>): void {
      state.dialogInfo = action.payload;
    },

    setDeleteFlag(state: InventoryJobState, action: PayloadAction<boolean>): void {
      state.deleteFlag = action.payload;
    },

    setAbortFlag(state: InventoryJobState, action: PayloadAction<boolean>): void {
      state.abortFlag = action.payload;
    },

    addRow(state: InventoryJobState): void {
      const tomorrow = moment().startOf('day').add(1, 'day');
      const twoWeeksLater = moment().startOf('day').add(1, 'day').add(2, 'week');
      const fourWeeksLater = moment().startOf('day').add(1, 'day').add(4, 'week');
      const emptyRow: InventoryJob = {
        uuid: uuid(),
        firstMailDate: tomorrow.toISOString(),
        firstMailCount: null,
        secondMailDate: twoWeeksLater.toISOString(),
        secondMailCount: null,
        lastDeleteDate: fourWeeksLater.toISOString(),
        lastDeleteCount: null,
        isActived: true,
        jobStatus: '0',
        isFirstSent: false,
        isSecondSent: false,
        isLastDeleted: false,
      };
      const newRowData: InventoryJob[] = [emptyRow].concat(state.inventoryJobRowData);
      state.inventoryJobRowData = newRowData;
    },

    getInventoryJobs(state: InventoryJobState, action: PayloadAction<InventoryJob[]>) {
      state.isLoading = false;
      state.hasError = false;
      const inventoryJobs = action.payload;
      state.inventoryJobRowData = inventoryJobs;
    },

    getUserCheckCount(state: InventoryJobState, action: PayloadAction<UserCheckCount[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.userCheckCount = action.payload[0].count;
    },

    createInventoryJob(state: InventoryJobState, action: PayloadAction<{ org: InventoryJob; new: InventoryJob }>): void {
      state.isLoading = false;
      state.hasError = false;
      const inventoryJobDataOrg = action.payload.org;
      const inventoryJobDataNew = action.payload.new;
      /**
       * ユーザが入力したデータは一旦削除
       */
      state.inventoryJobRowData = state.inventoryJobRowData.filter((row: InventoryJob) => row.uuid !== inventoryJobDataOrg.uuid);
      /**
       * サーバから返ってきた新規登録データを追加
       */
      state.inventoryJobRowData = [inventoryJobDataNew].concat(state.inventoryJobRowData);
    },

    updateInventoryJob(state: InventoryJobState, action: PayloadAction<InventoryJob>): void {
      state.isLoading = false;
      state.hasError = false;
      // console.log('payload: ', action.payload);
      const inventoryJobData: InventoryJob = action.payload;
      state.inventoryJobRowData = state.inventoryJobRowData.map((row: InventoryJob) => {
        if (row.id === inventoryJobData.id) {
          return inventoryJobData;
        } else {
          return row;
        }
      });
    },

    deleteInventoryJob(state: InventoryJobState, action: PayloadAction<InventoryJob[]>): void {
      state.isLoading = false;
      state.hasError = false;
      const inventoryJob = action.payload[0];
      state.inventoryJobRowData = state.inventoryJobRowData.filter((row: InventoryJob) => {
        // ユーザが新規追加したもの（uuidがあるもの）はcodeやnameを確認せず削除
        if (row.uuid) {
          if (row.uuid === inventoryJob.uuid) {
            return false;
          } else {
            return true;
          }
        } else {
          // 既存データはidで一意に決めて削除
          if (row.id === inventoryJob.id) {
            return false;
          } else {
            return true;
          }
        }
      });
    },
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const addRow =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.addRow());
  };

export const setDeleteFlag =
  (flag: boolean): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDeleteFlag(flag));
  };

export const setAbortFlag =
  (flag: boolean): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setAbortFlag(flag));
  };

export const abortJob =
  (editData: InventoryJob): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    // console.log('abort job');
    try {
      const id = editData.id;
      const data = {
        id: id,
      };
      const response = await axiosInstance.put<InventoryJob>(
        amplifyConfig.apiGateway.URL + '/usercheck/checkJobsCancel/' + id,
        // @ts-ignore
        data,
      );
      // console.log(response.data);
      const message: string = 'ジョブを中止しました';
      dispatch(slice.actions.setServerMessage(message));
      // @ts-ignore
      dispatch(slice.actions.updateInventoryJob(response.data));
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          // console.log(err.response);
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const getInventoryJobs =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axiosInstance.get<InventoryJob[]>(amplifyConfig.apiGateway.URL + '/usercheck/checkJobs');
      dispatch(slice.actions.getInventoryJobs(response.data));
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const getUserCheckCount =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axiosInstance.get<UserCheckCount[]>(amplifyConfig.apiGateway.URL + '/usercheck/userCheckCount');
      if (response.data[0].count === '') {
        const message: string = '対象ユーザがいません';
        dispatch(slice.actions.hasError(message));
      } else {
        dispatch(slice.actions.getUserCheckCount(response.data));
      }
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const searchInventoryJobs =
  (date: string, operator: DateComparisonOperator): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    // console.log(date, operator);
    try {
      const response = await axiosInstance.get<InventoryJob[]>(amplifyConfig.apiGateway.URL + '/usercheck/checkJobs', {
        params: {
          date: date,
          condition: operator,
        },
      });
      if (response.data.length === 0) {
        const message: string = '検索条件に合うデータがありません';
        dispatch(slice.actions.hasError(message));
      } else {
        dispatch(slice.actions.getInventoryJobs(response.data));
      }
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const editInventoryJob =
  (editKey: string, editValue: string | boolean, editData: InventoryJob): AppThunk =>
  (dispatch): void => {
    // console.log(editKey, editValue);
    const newEditData = Object.assign({}, editData);
    newEditData[editKey] = editValue;
    // editDataを更新
    dispatch(slice.actions.setEditData(newEditData));
    // Modalの中身を更新
    const rows: EditRow[] = Object.keys(editData)
      .map((key) => {
        return { key, value: newEditData[key] };
      })
      .filter((row) => row.key !== 'uuid')
      .filter((row) => row.key !== 'id')
      .filter((row) => row.key !== 'firstMailCount')
      .filter((row) => row.key !== 'secondMailCount')
      .filter((row) => row.key !== 'lastDeleteCount')
      .filter((row) => row.key !== 'isActived')
      .filter((row) => row.key !== 'jobStatus')
      .filter((row) => row.key !== 'isFirstSent')
      .filter((row) => row.key !== 'isSecondSent')
      .filter((row) => row.key !== 'isLastDeleted')
      .filter((row) => row.key !== 'createdBy')
      .filter((row) => row.key !== 'updatedBy')
      .filter((row) => row.key !== 'createdAt')
      .filter((row) => row.key !== 'updatedAt');
    dispatch(slice.actions.setDialogInfo(rows));
  };

export const createInventoryJob =
  (editData: InventoryJob): AppThunk =>
  (dispatch): void => {
    // console.log('create: ', editData);
    // editDataをテーブル表示するための準備
    // const { t } = useTranslation();
    const rows: EditRow[] = Object.keys(editData)
      .map((key) => {
        return { key, value: editData[key] };
      })
      .filter((row) => row.key !== 'uuid')
      .filter((row) => row.key !== 'id')
      .filter((row) => row.key !== 'firstMailCount')
      .filter((row) => row.key !== 'secondMailCount')
      .filter((row) => row.key !== 'lastDeleteCount')
      .filter((row) => row.key !== 'isActived')
      .filter((row) => row.key !== 'jobStatus')
      .filter((row) => row.key !== 'isFirstSent')
      .filter((row) => row.key !== 'isSecondSent')
      .filter((row) => row.key !== 'isLastDeleted')
      .filter((row) => row.key !== 'createdBy')
      .filter((row) => row.key !== 'updatedBy')
      .filter((row) => row.key !== 'createdAt')
      .filter((row) => row.key !== 'updatedAt');
    dispatch(slice.actions.setEditMode('create'));
    dispatch(slice.actions.setEditData(editData));
    dispatch(slice.actions.setDialogTitle('Create InventoryJob?'));
    dispatch(slice.actions.setDialogInfo(rows));
    dispatch(slice.actions.openDialog());
  };

export const updateInventoryJob =
  (editData: InventoryJob): AppThunk =>
  (dispatch): void => {
    // console.log('update: ', editData);
    // editDataをテーブル表示するための準備
    const rows: EditRow[] = Object.keys(editData)
      .map((key) => {
        return { key, value: editData[key] };
      })
      .filter((row) => row.key !== 'uuid')
      .filter((row) => row.key !== 'id')
      .filter((row) => row.key !== 'firstMailCount')
      .filter((row) => row.key !== 'secondMailCount')
      .filter((row) => row.key !== 'lastDeleteCount')
      .filter((row) => row.key !== 'isActived')
      .filter((row) => row.key !== 'jobStatus')
      .filter((row) => row.key !== 'isFirstSent')
      .filter((row) => row.key !== 'isSecondSent')
      .filter((row) => row.key !== 'isLastDeleted')
      .filter((row) => row.key !== 'createdBy')
      .filter((row) => row.key !== 'updatedBy')
      .filter((row) => row.key !== 'createdAt')
      .filter((row) => row.key !== 'updatedAt');
    dispatch(slice.actions.setEditMode('update'));
    dispatch(slice.actions.setEditData(editData));
    dispatch(slice.actions.setDialogTitle('Update InventoryJob?'));
    dispatch(slice.actions.setDialogInfo(rows));
    dispatch(slice.actions.openDialog());
  };

export const deleteInventoryJob =
  (editData: InventoryJob): AppThunk =>
  (dispatch): void => {
    // console.log('delete: ', editData);
    // editDataをテーブル表示するための準備
    const rows: EditRow[] = Object.keys(editData)
      .map((key) => {
        return { key, value: editData[key] };
      })
      .filter((row) => row.key !== 'uuid')
      .filter((row) => row.key !== 'id')
      .filter((row) => row.key !== 'firstMailCount')
      .filter((row) => row.key !== 'secondMailCount')
      .filter((row) => row.key !== 'lastDeleteCount')
      .filter((row) => row.key !== 'isActived')
      .filter((row) => row.key !== 'jobStatus')
      .filter((row) => row.key !== 'isFirstSent')
      .filter((row) => row.key !== 'isSecondSent')
      .filter((row) => row.key !== 'isLastDeleted')
      .filter((row) => row.key !== 'createdBy')
      .filter((row) => row.key !== 'updatedBy')
      .filter((row) => row.key !== 'createdAt')
      .filter((row) => row.key !== 'updatedAt');
    dispatch(slice.actions.setEditMode('delete'));
    dispatch(slice.actions.setEditData(editData));
    dispatch(slice.actions.setDialogTitle('Delete InventoryJob?'));
    dispatch(slice.actions.setDialogInfo(rows));
    dispatch(slice.actions.openDialog());
  };

export const editAction =
  (editMode: EditMode, editData: InventoryJob): AppThunk =>
  async (dispatch): Promise<void> => {
    /**
     * Create InventoryJob
     */
    if (editMode === 'create' && editData) {
      try {
        const userArributes = await fetchUserAttributes();
        const userEmail = userArributes?.email;
        const params: InventoryJob = {
          firstMailDate: editData.firstMailDate || undefined,
          secondMailDate: editData.secondMailDate || undefined,
          lastDeleteDate: editData.lastDeleteDate || undefined,
          isActived: editData.isActived === false ? false : editData.isActived === true ? true : undefined,
          createdBy: userEmail,
          updatedBy: userEmail,
        };
        // console.log('create params: ', params);
        const response = await axiosInstance.post<InventoryJob>(amplifyConfig.apiGateway.URL + '/usercheck/checkJob', params);

        const message: string = 'ジョブを作成しました';
        dispatch(slice.actions.setServerMessage(message));
        dispatch(slice.actions.createInventoryJob({ org: editData, new: response.data }));
      } catch (err) {
        if (err.isAxiosError) {
          // err: AxiosError
          if (err.response) {
            const serverResponse: ErrorResponse = err.response.data;
            dispatch(slice.actions.hasError(serverResponse.message));
          } else {
            dispatch(slice.actions.hasError(err.message));
          }
        } else {
          // err: Error
          dispatch(slice.actions.hasError(err.message));
        }
      }

      /**
       * Update InventoryJob
       */
    } else if (editMode === 'update' && editData) {
      try {
        const id = editData.id;
        const data = {
          firstMailDate: editData.firstMailDate || undefined,
          secondMailDate: editData.secondMailDate || undefined,
          lastDeleteDate: editData.lastDeleteDate || undefined,
          isActived: editData.isActived === false ? false : editData.isActived === true ? true : undefined,
        };
        // console.log('update params: ', params);
        const response = await axiosInstance.put<InventoryJob>(amplifyConfig.apiGateway.URL + '/usercheck/checkJobs/' + id, data);
        // console.log(response.data);
        const message: string = 'ジョブを更新しました';
        dispatch(slice.actions.setServerMessage(message));
        dispatch(slice.actions.updateInventoryJob(response.data));
      } catch (err) {
        if (err.isAxiosError) {
          // err: AxiosError
          if (err.response) {
            const serverResponse: ErrorResponse = err.response.data;
            dispatch(slice.actions.hasError(serverResponse.message));
          } else {
            dispatch(slice.actions.hasError(err.message));
          }
        } else {
          // err: Error
          dispatch(slice.actions.hasError(err.message));
        }
      }

      /**
       * Delete InventoryJob
       */
    } else if (editMode === 'delete' && editData) {
      // 新規データの場合
      if (editData.uuid) {
        dispatch(slice.actions.deleteInventoryJob([editData]));
        // 既存データの場合
      } else {
        try {
          const id = editData.id;
          // console.log('delete data: ', editData);
          const response = await axiosInstance.delete(amplifyConfig.apiGateway.URL + '/usercheck/checkJobs/' + id);

          const message: string = 'ジョブを削除しました';
          dispatch(slice.actions.setServerMessage(message));
          dispatch(slice.actions.deleteInventoryJob(response.data));
        } catch (err) {
          if (err.isAxiosError) {
            // err: AxiosError
            if (err.response) {
              const serverResponse: ErrorResponse = err.response.data;
              dispatch(slice.actions.hasError(serverResponse.message));
            } else {
              dispatch(slice.actions.hasError(err.message));
            }
          } else {
            // err: Error
            dispatch(slice.actions.hasError(err.message));
          }
        }
      }
    }
  };

export const setEditMode =
  (mode: EditMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setEditMode(mode));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const setDialogTitle =
  (title: string): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogTitle(title));
  };

export const setDialogInfo =
  (info: EditRow[]): AppThunk =>
  (dispatch): void => {
    // console.log(info)
    dispatch(slice.actions.setDialogInfo(info));
  };

export default slice;
